/*
This code was copied from https://github.com/marnusw/date-fns-tz

We could not update our version of date-fns-tz without a lot of work so I copied
everything that I needed and removed any legacy handling. I have provided the
license text from that project here.

Delete this file once we can update date-fns-tz to get `fromZonedTime()`
*/

/*
The MIT License (MIT)

Copyright © 2018 Marnus Weststrate

Permission is hereby granted, free of charge, to any person obtaining a copy of
this software and associated documentation files (the “Software”), to deal in
the Software without restriction, including without limitation the rights to
use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
of the Software, and to permit persons to whom the Software is furnished to do
so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

export function fromZonedTime(date, timeZone) {
  const utcDate = toUtcDate(date)
  const utc = utcDate.getTime()

  const offsetMilliseconds = tzParseTimezone(timeZone, utcDate)

  return new Date(utc + offsetMilliseconds)
}

function toUtcDate(date) {
  return newDateUTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  )
}

export function newDateUTC(
  fullYear,
  month,
  day,
  hour,
  minute,
  second,
  millisecond,
) {
  const utcDate = new Date(0)
  utcDate.setUTCFullYear(fullYear, month, day)
  utcDate.setUTCHours(hour, minute, second, millisecond)
  return utcDate
}

export function tzParseTimezone(timezone, date, isUtcDate) {
  // Empty string
  if (!timezone) {
    return 0
  }

  // IANA time zone
  date = new Date(date || Date.now())
  const utcDate = isUtcDate ? date : toUtcDate(date)

  const offset = calcOffset(utcDate, timezone)

  const fixedOffset = isUtcDate ? offset : fixOffset(date, offset, timezone)

  return -fixedOffset
}

function tzTokenizeDate(date, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    hourCycle: 'h23',
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  return partsOffset(dtf, date)
}

const typeToPos = {
  year: 0,
  month: 1,
  day: 2,
  hour: 3,
  minute: 4,
  second: 5,
}

function partsOffset(dtf, date) {
  try {
    const formatted = dtf.formatToParts(date)
    const filled = []
    for (let i = 0; i < formatted.length; i++) {
      const pos = typeToPos[formatted[i].type]

      if (pos !== undefined) {
        filled[pos] = parseInt(formatted[i].value, 10)
      }
    }
    return filled
  } catch (error) {
    if (error instanceof RangeError) {
      return [NaN]
    }
    throw error
  }
}

function calcOffset(date, timezone) {
  const tokens = tzTokenizeDate(date, timezone)

  // ms dropped because it's not provided by tzTokenizeDate
  const asUTC = newDateUTC(
    tokens[0],
    tokens[1] - 1,
    tokens[2],
    tokens[3] % 24,
    tokens[4],
    tokens[5],
    0,
  ).getTime()

  let asTS = date.getTime()
  const over = asTS % 1000
  asTS -= over >= 0 ? over : 1000 + over
  return asUTC - asTS
}

function fixOffset(date, offset, timezone) {
  const localTS = date.getTime()

  // Our UTC time is just a guess because our offset is just a guess
  let utcGuess = localTS - offset

  // Test whether the zone matches the offset for this ts
  const o2 = calcOffset(new Date(utcGuess), timezone)

  // If so, offset didn't change, and we're done
  if (offset === o2) {
    return offset
  }

  // If not, change the ts by the difference in the offset
  utcGuess -= o2 - offset

  // If that gives us the local time we want, we're done
  const o3 = calcOffset(new Date(utcGuess), timezone)
  if (o2 === o3) {
    return o2
  }

  // If it's different, we're in a hole time. The offset has changed, but we don't adjust the time
  return Math.max(o2, o3)
}

export function toZonedTime(date, timeZone) {
  const offsetMilliseconds = tzParseTimezone(timeZone, date, true)

  const d = new Date(date.getTime() - offsetMilliseconds)

  const resultDate = new Date(0)

  resultDate.setFullYear(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())

  resultDate.setHours(
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds(),
  )

  return resultDate
}
